
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { Conversation } from "@/types/conversation";
import { formatCurrency, getCurrencyByCode } from "@/utils/currency-data";

/**
 * Creates a new PDF document with basic styling and structure
 */
export const createPdfDocument = (): jsPDF => {
  // Create a new PDF document
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4"
  });
  
  // Set base font
  doc.setFont("helvetica");
  
  return doc;
};

/**
 * Adds case information to the PDF document
 */
export const addCaseInformation = (
  doc: jsPDF, 
  conversation: Conversation,
  userFullName: string
): number => {
  // Add document title
  doc.setFontSize(18);
  doc.text("Refund Request Conversation", 14, 20);
  
  // Add case information
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Case Information:", 14, 32);
  doc.setFont("helvetica", "normal");
  
  // Format case reference with CASE prefix
  const caseReference = `CASE-${conversation.id.substring(0, 8).toUpperCase()}`;
  
  // Case details table
  const caseDetailsBody = [
    ["Case Reference:", caseReference],
    ["Customer Name:", userFullName],
    ["Merchant:", conversation.merchant || "Not specified"],
    ["Date:", format(new Date(), "yyyy-MM-dd")],
    ["Region:", conversation.region || "International"],
  ];
  
  autoTable(doc, {
    startY: 35,
    body: caseDetailsBody,
    theme: "plain",
    styles: {
      fontSize: 10,
      cellPadding: 2,
    },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: "bold" },
      1: { cellWidth: 100 }
    }
  });

  // Return the Y position after the case information table
  return (doc as any).lastAutoTable.finalY + 8;
};

/**
 * Adds refund request details to the PDF document
 */
export const addRefundDetails = (
  doc: jsPDF, 
  conversation: Conversation,
  startY: number
): number => {
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Refund Request Details:", 14, startY);
  doc.setFont("helvetica", "normal");
  
  // Format amount with currency
  const amountString = conversation.amount 
    ? formatCurrency(conversation.amount, conversation.currency || 'USD')
    : "Not specified";
  
  // Purchase details table
  const refundDetailsBody = [
    ["Product:", conversation.product || "Not specified"],
    ["Order Number:", conversation.order_number || "Not specified"],
    ["Amount:", amountString],
    ["Currency:", conversation.currency || "USD"],
    ["Issue:", conversation.issue || "Not specified"],
    ["Purchase Date:", conversation.purchase_date ? format(new Date(conversation.purchase_date), "yyyy-MM-dd") : "Not specified"],
  ];
  
  autoTable(doc, {
    startY: startY + 3,
    body: refundDetailsBody,
    theme: "plain",
    styles: {
      fontSize: 10,
      cellPadding: 2,
    },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: "bold" },
      1: { cellWidth: 100 }
    }
  });

  // Return the Y position after the refund details table
  return (doc as any).lastAutoTable.finalY + 10;
};

/**
 * Adds a footer note to the PDF document
 */
export const addFooterNote = (doc: jsPDF, finalY: number): void => {
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(
    "This is an official record of correspondence regarding your refund request. " +
    "If your refund has been denied, you may submit this PDF as supporting evidence to your payment provider.",
    14, finalY + 10, { maxWidth: 180 }
  );
};

/**
 * Generates a filename for the PDF export
 */
export const generatePdfFilename = (conversation: Conversation): string => {
  const merchantName = (conversation.merchant || "Merchant").replace(/\s+/g, "-").toLowerCase();
  return `refund-request-${merchantName}-${format(new Date(), "yyyy-MM-dd")}.pdf`;
};
